import {reachGoal} from 'utils/metrica';
import {formatReferenceCopy} from 'utils/typography';
import React, {useEffect, useState} from "react";
import { useTranslation } from "react-i18next";
import {useForm, Controller} from "react-hook-form";

import {CertificationType} from "../../index";
import RadioTemplate from "../radio-template";
import {ADORNMENTS, COLORS} from "../../../../../../../config/constants";
import {checkDecimalsAfterDot} from "../../../../../../../utils/validation";

import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import { Check as CheckIcon } from '@vedmarket/ui-kit';
import Typography from "@mui/material/Typography";

import {
  useSetCompletedByOrderIdMutation,
  useSetManualCertificationMutation,
  useSummaryCompleted
} from "../../../../../../../api/calc";
import {
  useFormattedDecimalInput,
  useGetCalcIdFromRoute,
} from "../../../../../../../utils/hooks";
import { Completed } from '@vedmarket/ui-kit';
import MenuButton from '../../../../../../common/menuButton';
import {isCalculationViewModeError, numberWithSpaces} from "../../../../../../../utils/helpers";
import cx from 'classnames';

import { readOnlyButtonEndIcon } from 'components/common/read-only-action-icon';
import 'components/common/read-only-action-button.scss';

import {useCalculationReadOnly} from '../../../../calculation-read-only';

import './manual.scss';

enum CERTIFICATION_MANUAL_FORM {
  CERT_TOTAL_MANUAL = 'cert_total_manual',
}

type CertificationManualForm = {
  cert_total_manual: number
}

type Props = {
  isExpanded: boolean
  value?: number
  isActive: boolean
  handleChangeAccordion(value: CertificationType, isExpanded: boolean): void
  handleComplete(): void
  handleChange(): void
}

const CertificationManual = ({ isExpanded, handleChangeAccordion, handleComplete, handleChange, value, isActive }: Props) => {

  const readOnly = useCalculationReadOnly()

  const { t } = useTranslation();


  const orderId = useGetCalcIdFromRoute()

  const [ shouldShowCompletedInfo, setShouldShowCompletedInfo ] = useState(Boolean(value));

  const errorNumber = t('errors__message__incorrectValue');

  const [ setManualCertification ] = useSetManualCertificationMutation()

  const [ setCompleted ] = useSetCompletedByOrderIdMutation()

  const certificationIsCompleted = useSummaryCompleted()?.certification;

  const { register, handleSubmit, setValue, control, getValues, formState: { errors, isValid } } = useForm<CertificationManualForm>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      [CERTIFICATION_MANUAL_FORM.CERT_TOTAL_MANUAL]: value,
    }
  })

  const decimalInput = useFormattedDecimalInput();

  useEffect(() => {
    if (value) {
      // @ts-ignore
      setValue(CERTIFICATION_MANUAL_FORM.CERT_TOTAL_MANUAL, value);
    }
    setShouldShowCompletedInfo(Boolean(value && certificationIsCompleted));
  }, [ value ])

  const handleOpenAccordion = (isExpanded: boolean) => {
    handleChangeAccordion(CertificationType.MANUAL, isExpanded);
  }

  const onSubmit = handleSubmit(async values => {
    const result = await setManualCertification({
      orderId,
      cert_total_manual: values.cert_total_manual
    });

    if (isCalculationViewModeError(result)) {
      return;
    }

    const completedResult = await setCompleted({
      orderId,
      certification: true
    })

    if (isCalculationViewModeError(completedResult)) {
      return;
    }

    setShouldShowCompletedInfo(true);
    handleComplete();
  })

  const getCompletedInfo = () => [
    {
      label: formatReferenceCopy(t('dictionaries__text__certification__priceManual')),
      value: `${numberWithSpaces(value)} USD`
    }
  ];

  return <div className='certification-manual'>
    <RadioTemplate
      isActive={isActive}
      title={<Typography variant='h6' sx={{
        color: COLORS.BLACK_DARK,
        fontSize: '20px',
        fontWeight: 700,
        lineHeight: '26px',
        letterSpacing: 0,
        paddingLeft: '16px',
        gap: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
      }}>
        {formatReferenceCopy(t('dictionaries__text__certification__iKnowTheCostOfCertification'))}
        <Typography component='span' variant='inherit' sx={{
          fontWeight: 400,
          flexShrink: 0,
          textAlign: 'right',
        }}>
          {value ? `${numberWithSpaces(value)} USD` : undefined}
        </Typography>
      </Typography>}
      value={CertificationType.MANUAL}
      isExpanded={isExpanded}
      handleChangeAccordion={handleOpenAccordion}
    >
      {shouldShowCompletedInfo ? (
        <Completed
          info={getCompletedInfo()}
          onEdit={() => setShouldShowCompletedInfo(false)}
          MenuComponent={MenuButton}
          isFullRow
          caption={formatReferenceCopy(t('dictionaries__text__certification__theCostOfCertificationIsSpecifiedManually'))}
        />
      ) : <>
        <div className='certification-manual__container'>
          <Controller
            name={CERTIFICATION_MANUAL_FORM.CERT_TOTAL_MANUAL}
            control={control}
            rules={{
              required: true,
              validate: (value) => checkDecimalsAfterDot(value, errorNumber),
              min: { value: 0, message: errorNumber },
            }}
            render={({ field, fieldState }) => (
              <TextField
                disabled={readOnly}
                sx={{width: '100%'}}
                value={decimalInput.getDisplayValue({
                  fieldName: CERTIFICATION_MANUAL_FORM.CERT_TOTAL_MANUAL,
                  rawValue: field.value,
                })}
                onChange={(e) => {
                  decimalInput.startEditing(CERTIFICATION_MANUAL_FORM.CERT_TOTAL_MANUAL);
                  const value = e.target.value;
                  const valueWithDot = value.replace(',', '.');
                  field.onChange(valueWithDot);
                }}
                onBlur={() => {
                  field.onBlur();
                  decimalInput.stopEditing(CERTIFICATION_MANUAL_FORM.CERT_TOTAL_MANUAL);
                  handleChange()
                }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                variant='outlined'
                label={formatReferenceCopy(t('dictionaries__text__certification__priceManual'))}
                InputLabelProps={{ shrink: true }}
                InputProps={{ endAdornment: ADORNMENTS.PRICE }}
              />
            )}
          />
        </div>
        <Button
          variant='contained'
          className={cx({ 'read-only-action-button': readOnly })}
          startIcon={<CheckIcon />}
          endIcon={readOnlyButtonEndIcon(readOnly)}
          sx={{ marginTop: 3 }}
          fullWidth
          disabled={readOnly || !isValid}
          onClick={readOnly ? undefined : (event) => {
            reachGoal({
              goal: 'stoimost_sertifikacii',
              params: { cert_total_manual: getValues(CERTIFICATION_MANUAL_FORM.CERT_TOTAL_MANUAL) },
            });
            onSubmit(event);
          }}
          title={readOnly ? t('calc__message__lockedByApplication') : undefined}
        >
          <Typography component='span' variant='inherit'>
            {formatReferenceCopy(t('dictionaries__text__certification__saveTheCostOfCertification'))}
          </Typography>
        </Button>
      </>}
    </RadioTemplate>
  </div>
}

export default CertificationManual
